// src/SwiftCodeFinder.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Search,
  ChevronDown,
  Copy,
  AlertTriangle,
  Loader,
} from 'lucide-react';

import {
  tractSwiftCodeFinderUsedEvent,
  SwiftCodeFinderUsedEvent,
} from '../../utlis/useAnalytics'; 

/* ────────────────────────────
   Types & Helpers
──────────────────────────── */
interface SwiftCodeData {
  country: string;
  bankName: string;
  bankBranch: string;
  swiftCode: string;
  address: string;
  city: string;
}

const SAMPLE_DATA: SwiftCodeData[] = [
  {
    country: 'India',
    bankName: 'State Bank of India',
    bankBranch: 'State Bank of India – Main Branch',
    swiftCode: 'SBININBB104',
    address: '11, Sansad Marg, New Delhi',
    city: 'New Delhi',
  },
  {
    country: 'India',
    bankName: 'HDFC Bank',
    bankBranch: 'HDFC Bank – Nariman Point',
    swiftCode: 'HDFCINBB',
    address: 'HDFC Bank House, Senapati Bapat Marg, Lower Parel',
    city: 'Mumbai',
  },
];

const uniq = (arr: string[]) =>
  Array.from(new Set(arr)).filter(Boolean).sort();

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-');

const matchSlug = (slug: string, opts: string[]) =>
  opts.find((o) => slugify(o) === slug.toLowerCase()) || '';

const BASE_PATH = '/swift-code-finder';

/* ────────────────────────────
   Component
──────────────────────────── */
const SwiftCodeFinder: React.FC = () => {
  const location = useLocation(); // for deep-linking

  /* ───────── State ───────── */
  const [allData, setAllData] = useState<SwiftCodeData[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [banks, setBanks] = useState<string[]>([]);
  const [rows, setRows] = useState<SwiftCodeData[]>([]);

  // filters
  const [selCountry, setSelCountry] = useState('');
  const [selCity, setSelCity] = useState('');
  const [selBank, setSelBank] = useState('');
  const [search, setSearch] = useState('');

  // dropdown search inputs
  const [cTry, setCTry] = useState('');
  const [cTy, setCTy] = useState('');
  const [bNk, setBNk] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [hasSearch, setHasSearch] = useState(false);
  const [openCtry, setOpenCtry] = useState(false);
  const [openCity, setOpenCity] = useState(false);
  const [openBank, setOpenBank] = useState(false);

  // refs for outside-click
  const refCtry = useRef<HTMLDivElement>(null);
  const refCity = useRef<HTMLDivElement>(null);
  const refBank = useRef<HTMLDivElement>(null);

  /* ───── Load from API ───── */
  useEffect(() => {
    setLoading(true);
    fetch('https://json-api-vercel-two.vercel.app/api/swift-codes.js')
      .then((res) => {
        if (!res.ok) throw new Error(`Network response was not ok: ${res.statusText}`);
        return res.json();
      })
      .then((data: any[]) => {
        if (!Array.isArray(data)) throw new Error('Invalid data format');
        const formatted: SwiftCodeData[] = data.map((item) => ({
          country: item.Country || item.country || '',
          bankName: item.Bank || item.bankName || '',
          bankBranch: item['Bank Branch'] || item.bankBranch || '',
          swiftCode: item['Swift Code'] || item.swiftCode || '',
          address: item.Address || item.address || '',
          city: item.City || item.city || '',
        }));
        if (!formatted.length) throw new Error('No records');
        setAllData(formatted);
        setCountries(uniq(formatted.map((d) => d.country)));
      })
      .catch((err) => {
        console.error('Fetch error:', err);
        setError('Failed to load SWIFT codes from API. Using the sample data.');
        setAllData(SAMPLE_DATA);
        setCountries(uniq(SAMPLE_DATA.map((d) => d.country)));
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  /* ───── Deep-link parsing ───── */
  useEffect(() => {
    if (!allData.length) return;
    const path = location.pathname;
    if (!path.startsWith(BASE_PATH)) return;
    const rest = path.slice(BASE_PATH.length + 1);
    if (!rest) return;
    const [cSlug, bSlug, ciSlug] = rest.split('/');
    const c = matchSlug(cSlug, countries);
    if (!c) return;

    const byCountry = allData.filter((d) => d.country === c);
    const bankOpts = uniq(byCountry.map((d) => d.bankName));
    const cityOpts = uniq(byCountry.map((d) => d.city));

    const b = bSlug ? matchSlug(bSlug, bankOpts) : '';
    const ci = ciSlug ? matchSlug(ciSlug, cityOpts) : '';

    setSelCountry(c);
    if (b) setSelBank(b);
    if (ci) setSelCity(ci);
    setHasSearch(true);
  }, [allData, countries, location.pathname]);

  /* ───── Rebuild city & bank dropdowns ───── */
  useEffect(() => {
    const byCountry = selCountry
      ? allData.filter((d) => d.country === selCountry)
      : allData;
    setCities(uniq(byCountry.map((d) => d.city)));
    if (selCity && !byCountry.some((d) => d.city === selCity)) setSelCity('');

    const byCity = selCity
      ? byCountry.filter((d) => d.city === selCity)
      : byCountry;
    setBanks(uniq(byCity.map((d) => d.bankName)));
    if (selBank && !byCity.some((d) => d.bankName === selBank)) setSelBank('');
  }, [selCountry, selCity, allData]);

  /* ───── Filter table rows ───── */
  useEffect(() => {
    let arr = [...allData];
    if (selCountry) arr = arr.filter((d) => d.country === selCountry);
    if (selCity) arr = arr.filter((d) => d.city === selCity);
    if (selBank) arr = arr.filter((d) => d.bankName === selBank);
    if (search) {
      const t = search.toLowerCase();
      arr = arr.filter((d) =>
        [
          d.bankName,
          d.bankBranch,
          d.swiftCode,
          d.address,
          d.city,
          d.country,
        ].some((f) => f.toLowerCase().includes(t))
      );
    }
    setRows(arr);
    setHasSearch(!!(selCountry || selCity || selBank || search));
  }, [allData, selCountry, selCity, selBank, search]);

  /* ───── Sync URL slug ───── */
  useEffect(() => {
    const parts = [selCountry, selBank, selCity]
      .filter(Boolean)
      .map(slugify);
    const newPath = parts.length
      ? `${BASE_PATH}/${parts.join('/')}`
      : BASE_PATH;
    if (window.location.pathname !== newPath) {
      window.history.replaceState(null, '', newPath);
    }
  }, [selCountry, selBank, selCity]);

  /* ───── Close dropdowns on outside click ───── */
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (refCtry.current && !refCtry.current.contains(e.target as Node))
        setOpenCtry(false);
      if (refCity.current && !refCity.current.contains(e.target as Node))
        setOpenCity(false);
      if (refBank.current && !refBank.current.contains(e.target as Node))
        setOpenBank(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  /* ───── Helpers ───── */
  const onCopy = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(code);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const reset = () => {
    setSelCountry('');
    setSelCity('');
    setSelBank('');
    setSearch('');
    setCTry('');
    setCTy('');
    setBNk('');
    setHasSearch(false);
  };

  const hasCrit = selCountry || selCity || selBank || search;
  const fCountries = countries.filter((v) =>
    v.toLowerCase().includes(cTry.toLowerCase())
  );
  const fCities = cities.filter((v) =>
    v.toLowerCase().includes(cTy.toLowerCase())
  );
  const fBanks = banks.filter((v) =>
    v.toLowerCase().includes(bNk.toLowerCase())
  );

  /* ───────── JSX ───────── */
  return (
    <section id="swift-finder" className="py-16 sm:py-20 mt-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#27a887] mb-6">
              SWIFT Code Finder
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Find the correct SWIFT/BIC code for international wire transfers
              quickly and easily.
            </p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
            {/* Dropdown Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-6">
              {/* Country */}
              <div ref={refCtry}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country
                </label>
                <div className="relative">
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => setOpenCtry(!openCtry)}
                    className={`relative w-full flex items-center justify-between border ${
                      openCtry ? 'border-[#1c9e86]' : 'border-gray-300'
                    } rounded-lg shadow-sm p-3 bg-white text-left focus:outline-none`}
                  >
                    {loading ? (
                      <span className="flex items-center text-gray-500">
                        <Loader
                          size={16}
                          className="animate-spin mr-2"
                        />
                        Loading…
                      </span>
                    ) : (
                      <span
                        className={selCountry ? 'text-gray-800' : 'text-gray-500'}
                      >
                        {selCountry || 'Select a country'}
                      </span>
                    )}
                    <ChevronDown
                      size={18}
                      className={`transition-transform ${
                        openCtry
                          ? 'rotate-180 text-[#1c9e86]'
                          : 'text-gray-400'
                      }`}
                    />
                  </button>
                  {openCtry && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                      <div className="p-2 border-b">
                        <div className="flex items-center px-3 py-2 bg-gray-50 rounded-lg">
                          <Search size={18} className="text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search countries…"
                            className="w-full ml-2 bg-transparent text-sm focus:outline-none"
                            value={cTry}
                            onChange={(e) => setCTry(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="max-h-60 overflow-y-auto py-1">
                        {fCountries.length ? (
                          fCountries.map((c) => (
                            <div
                              key={c}
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                              onClick={() => {
                                setSelCountry(c);
                                setOpenCtry(false);
                                setCTry('');
                                // Track country selection
                                tractSwiftCodeFinderUsedEvent(
                                  SwiftCodeFinderUsedEvent.SelectedCountry,
                                  { country: c, city: '', bank: '' }
                                );
                              }}
                            >
                              {c}
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-3 text-center text-sm text-gray-500">
                            No match
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* City */}
              <div ref={refCity}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City
                </label>
                <div className="relative">
                  <button
                    type="button"
                    disabled={loading || !selCountry}
                    onClick={() => setOpenCity(!openCity)}
                    className={`relative w-full flex items-center justify-between border ${
                      openCity ? 'border-[#1c9e86]' : 'border-gray-300'
                    } rounded-lg shadow-sm p-3 bg-white text-left focus:outline-none`}
                  >
                    {loading ? (
                      <span className="flex items-center text-gray-500">
                        <Loader
                          size={16}
                          className="animate-spin mr-2"
                        />
                        Loading…
                      </span>
                    ) : (
                      <span
                        className={selCity ? 'text-gray-800' : 'text-gray-500'}
                      >
                        {selCity || 'Select a city'}
                      </span>
                    )}
                    <ChevronDown
                      size={18}
                      className={`transition-transform ${
                        openCity
                          ? 'rotate-180 text-[#1c9e86]'
                          : 'text-gray-400'
                      }`}
                    />
                  </button>
                  {openCity && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                      <div className="p-2 border-b">
                        <div className="flex items-center px-3 py-2 bg-gray-50 rounded-lg">
                          <Search size={18} className="text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search cities…"
                            className="w-full ml-2 bg-transparent text-sm focus:outline-none"
                            value={cTy}
                            onChange={(e) => setCTy(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="max-h-60 overflow-y-auto py-1">
                        {fCities.length ? (
                          fCities.map((ci) => (
                            <div
                              key={ci}
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                              onClick={() => {
                                setSelCity(ci);
                                setOpenCity(false);
                                setCTy('');
                                // Track city selection
                                tractSwiftCodeFinderUsedEvent(
                                  SwiftCodeFinderUsedEvent.SelectedCity,
                                  { country: selCountry, city: ci, bank: '' }
                                );
                              }}
                            >
                              {ci}
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-3 text-center text-sm text-gray-500">
                            No match
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Bank */}
              <div ref={refBank}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bank
                </label>
                <div className="relative">
                  <button
                    type="button"
                    disabled={loading || !selCountry}
                    onClick={() => setOpenBank(!openBank)}
                    className={`relative w-full flex items-center justify-between border ${
                      openBank ? 'border-[#1c9e86]' : 'border-gray-300'
                    } rounded-lg shadow-sm p-3 bg-white text-left focus:outline-none`}
                  >
                    {loading ? (
                      <span className="flex items-center text-gray-500">
                        <Loader
                          size={16}
                          className="animate-spin mr-2"
                        />
                        Loading…
                      </span>
                    ) : (
                      <span
                        className={selBank ? 'text-gray-800' : 'text-gray-500'}
                      >
                        {selBank || 'Select a bank'}
                      </span>
                    )}
                    <ChevronDown
                      size={18}
                      className={`transition-transform ${
                        openBank
                          ? 'rotate-180 text-[#1c9e86]'
                          : 'text-gray-400'
                      }`}
                    />
                  </button>
                  {openBank && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                      <div className="p-2 border-b">
                        <div className="flex items-center px-3 py-2 bg-gray-50 rounded-lg">
                          <Search size={18} className="text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search banks…"
                            className="w-full ml-2 bg-transparent text-sm focus:outline-none"
                            value={bNk}
                            onChange={(e) => setBNk(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="max-h-60 overflow-y-auto py-1">
                        {fBanks.length ? (
                          fBanks.map((b) => (
                            <div
                              key={b}
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                              onClick={() => {
                                setSelBank(b);
                                setOpenBank(false);
                                setBNk('');
                                // Track bank selection
                                tractSwiftCodeFinderUsedEvent(
                                  SwiftCodeFinderUsedEvent.SelectedBank,
                                  { country: selCountry, city: selCity, bank: b }
                                );
                              }}
                            >
                              {b}
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-3 text-center text-sm text-gray-500">
                            No match
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Search field */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search size={18} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search branch, SWIFT code, address…"
                  className="pl-10 block w-full border border-gray-300 rounded-lg p-3 focus:ring-[#1c9e86] focus:border-[#1c9e86]"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Active filters */}
            {hasSearch && hasCrit && (
              <div className="flex flex-wrap items-center mb-4 gap-2">
                <span className="text-sm text-gray-500">Active filters:</span>
                {selCountry && (
                  <span className="inline-flex px-3 py-1 rounded-full text-xs bg-[#ecf6f3] text-[#1c9e86]">
                    Country: {selCountry}
                  </span>
                )}
                {selBank && (
                  <span className="inline-flex px-3 py-1 rounded-full text-xs bg-[#ecf6f3] text-[#1c9e86]">
                    Bank: {selBank}
                  </span>
                )}
                {selCity && (
                  <span className="inline-flex px-3 py-1 rounded-full text-xs bg-[#ecf6f3] text-[#1c9e86]">
                    City: {selCity}
                  </span>
                )}
                {search && (
                  <span className="inline-flex px-3 py-1 rounded-full text-xs bg-[#ecf6f3] text-[#1c9e86]">
                    Search: {search}
                  </span>
                )}
                <button
                  onClick={reset}
                  className="text-xs text-gray-500 hover:text-[#1c9e86] underline ml-auto"
                >
                  Reset filters
                </button>
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div className="text-center p-12">
                <Loader
                  size={36}
                  className="animate-spin mx-auto mb-4 text-[#1c9e86]"
                />
                <p className="text-gray-600">Loading SWIFT codes…</p>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 mb-4">
                <div className="flex items-start">
                  <AlertTriangle size={20} className="mr-2 mt-0.5" />
                  <span className="text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* Results */}
            {!loading && hasSearch && rows.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {rows.length} {rows.length === 1 ? 'Result' : 'Results'} Found
                </h3>
                <div className="border border-gray-200 rounded-lg overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200 table-auto">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Country
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Bank
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Branch
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          SWIFT
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Address
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          City
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {rows.slice(0, 20).map((d, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-normal break-words text-sm text-gray-700">
                            {d.country}
                          </td>
                          <td className="px-4 py-4 whitespace-normal break-words text-sm text-gray-700">
                            {d.bankName}
                          </td>
                          <td className="px-4 py-4 whitespace-normal break-words text-sm text-gray-700">
                            {d.bankBranch}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                            <div className="flex items-center space-x-2">
                              <span className="font-mono">{d.swiftCode}</span>
                              <button
                                onClick={() => onCopy(d.swiftCode)}
                                className="text-gray-400 hover:text-[#1c9e86]"
                                title="Copy SWIFT code"
                              >
                                {copied === d.swiftCode ? (
                                  <span className="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded">
                                    Copied!
                                  </span>
                                ) : (
                                  <Copy size={14} />
                                )}
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-normal break-words text-sm text-gray-500">
                            {d.address}
                          </td>
                          <td className="px-4 py-4 whitespace-normal break-words text-sm text-gray-700">
                            {d.city}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {rows.length > 20 && (
                  <div className="mt-4 text-center text-sm text-gray-600">
                    Showing top 20 of {rows.length} results. Refine your filters for more specific matches.
                  </div>
                )}
              </div>
            )}

            {/* No results */}
            {!loading && hasSearch && rows.length === 0 && (
              <div className="py-12 text-center">
                <div className="bg-gray-50 rounded-lg p-8 max-w-md mx-auto">
                  <Search className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    No results found
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Try adjusting your search or filter criteria.
                  </p>
                  <button
                    onClick={reset}
                    className="text-[#1c9e86] font-medium hover:underline"
                  >
                    Reset all filters
                  </button>
                </div>
              </div>
            )}

            {/* Info box */}
            <div className="mt-8 p-4 bg-blue-50 rounded-lg text-sm text-blue-800 border border-blue-100">
              <div className="flex items-start">
                <AlertTriangle
                  size={20}
                  className="text-blue-500 flex-shrink-0 mr-3 mt-0.5"
                />
                <p>
                  <strong>What is a SWIFT Code?</strong> SWIFT codes (BIC)
                  uniquely identify banks for international transfers and ensure
                  your funds reach the correct institution. Please cross check
                  any SWIFT codes beforehand.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SwiftCodeFinder;
